<template>
  <Teleport to="body">
    <div class="usage-statistics-overlay" @click.self="close">
      <section
        ref="dialog"
        class="usage-statistics-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="usage-statistics-title"
        tabindex="-1"
        @keydown="handleKeydown"
      >
        <header class="statistics-header">
          <div>
            <h2 id="usage-statistics-title">사용시간 통계</h2>
            <p>
              현재 검색이 적용된 {{ scopedDevices.length.toLocaleString() }}대
              기준
            </p>
          </div>
          <button
            type="button"
            class="statistics-button"
            aria-label="사용시간 통계 닫기"
            @click="close"
          >
            닫기
          </button>
        </header>
        <nav
          v-if="!adminScope.restricted"
          class="statistics-breadcrumb"
          aria-label="통계 조회 위치"
        >
          <button
            type="button"
            :aria-current="!region ? 'page' : undefined"
            @click="goRegions"
          >
            전체 지역
          </button>
          <template v-if="region"
            ><span aria-hidden="true">›</span
            ><button
              type="button"
              :aria-current="!school ? 'page' : undefined"
              @click="goSchools"
            >
              {{ region.label }}
            </button></template
          >
          <template v-if="school"
            ><span aria-hidden="true">›</span
            ><span aria-current="page">{{ schoolTitle }}</span></template
          >
        </nav>
        <main ref="bodyPanel" class="statistics-body">
          <h3>{{ heading }}</h3>
          <p v-if="!deviceMode" class="statistics-note">
            전체 기기 수 대비 비율입니다. 이름 또는 막대를 선택하면 하위 통계를
            확인할 수 있습니다.
          </p>
          <p v-else class="statistics-note">
            총 사용시간 내림차순 · 기준선은 교체 검토를 위한 참고 정보입니다.
          </p>
          <p v-if="region && !adminScope.restricted" class="statistics-note">
            학교 식별 정보가 없는 경우 같은 지역·학교명으로 묶으므로 동명 학교를
            구분하지 못할 수 있습니다.
          </p>
          <ul
            v-if="!deviceMode"
            class="statistics-legend"
            aria-label="사용시간 구간"
          >
            <li v-for="bucket in legend" :key="bucket.label">
              <span
                :style="{ background: bucket.color }"
                aria-hidden="true"
              ></span
              >{{ bucket.label }}
            </li>
          </ul>
          <p v-if="unknownCount" class="statistics-note">
            사용시간 미확인 {{ unknownCount.toLocaleString() }}대: 누락되거나
            유효하지 않은 값입니다.
            {{
              deviceMode
                ? "시간 막대 없이 목록 끝에 표시합니다."
                : "전체 기기 수에 포함하고 회색 구간으로 표시합니다."
            }}
          </p>
          <template v-if="pageResult.total">
            <div class="statistics-chart-scroll">
              <div
                class="statistics-chart"
                :style="{
                  height: `${Math.max(
                    220,
                    pageResult.items.length * 46 + 85
                  )}px`
                }"
              >
                <Bar
                  :key="chartKey"
                  :data="chartData"
                  :options="chartOptions"
                  :plugins="chartPlugins"
                  role="img"
                  :aria-label="
                    heading +
                    '. 정확한 값과 선택 버튼은 아래 상세 표에 있습니다.'
                  "
                />
              </div>
            </div>
            <nav class="statistics-pagination" aria-label="통계 페이지">
              <span
                >총 {{ pageResult.total.toLocaleString()
                }}{{ deviceMode ? "대" : "개" }} · {{ pageResult.page }} /
                {{ pageResult.totalPages }}페이지</span
              >
              <button
                type="button"
                class="statistics-button"
                :disabled="pageResult.page === 1"
                @click="changePage(-1)"
              >
                이전
              </button>
              <button
                type="button"
                class="statistics-button"
                :disabled="pageResult.page === pageResult.totalPages"
                @click="changePage(1)"
              >
                다음
              </button>
            </nav>
            <details class="statistics-details" :open="!deviceMode">
              <summary>
                정확한 값 및 {{ deviceMode ? "기기" : "지역·학교 선택" }} 목록
              </summary>
              <div class="statistics-table-scroll">
                <table>
                  <caption class="statistics-sr-only">
                    {{
                      heading
                    }}
                    상세 값
                  </caption>
                  <thead>
                    <tr>
                      <th scope="col">
                        {{
                          deviceMode ? "기기 위치" : region ? "학교" : "지역"
                        }}
                      </th>
                      <template v-if="!deviceMode"
                        ><th scope="col">기기 수</th>
                        <th
                          v-for="bucket in legend"
                          :key="bucket.label"
                          scope="col"
                        >
                          {{ bucket.label }}
                        </th></template
                      >
                      <th v-else scope="col">총 사용시간</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(item, index) in pageResult.items"
                      :key="item.key || index"
                    >
                      <th scope="row">
                        <span v-if="deviceMode">{{ item.deviceLabel }}</span
                        ><button
                          v-else
                          type="button"
                          class="statistics-select"
                          @click="selectGroup(item)"
                        >
                          {{ groupTitle(item) }}
                        </button>
                      </th>
                      <template v-if="!deviceMode"
                        ><td>{{ item.total.toLocaleString() }}대</td>
                        <td
                          v-for="(bucket, bucketNumber) in legend"
                          :key="bucket.label"
                        >
                          {{ countAt(item, bucketNumber).toLocaleString() }}대
                          ({{
                            (
                              (countAt(item, bucketNumber) / item.total) *
                              100
                            ).toFixed(1)
                          }}%)
                        </td></template
                      >
                      <td v-else>{{ formatUsage(item.seconds) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </details>
          </template>
          <p v-else class="statistics-empty" role="status">
            현재 검색 조건에 해당하는 기기가 없습니다.
          </p>
        </main>
      </section>
    </div>
  </Teleport>
</template>

<script setup>
/* global defineProps, defineEmits */
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch
} from "vue";
import { Bar } from "vue-chartjs";
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend
} from "chart.js";
import {
  createLocalStatisticsProvider,
  paginate,
  USAGE_BUCKETS,
  UNKNOWN_BUCKET,
  schoolAxisMax,
  formatUsage
} from "@/utils/usageStatistics";
import {
  distributionChartData,
  deviceChartData,
  statisticsChartOptions,
  statisticsLabelsPlugin
} from "@/utils/usageStatisticsChart";

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);
import { getAdminScope, filterSchoolRows } from "@/utils/adminScope";
import { normalizeDevice } from "@/utils/usageStatistics";
const adminScope = getAdminScope();
// 학교 통계도 목록과 같은 조회 범위를 적용한다.
const scopedDevices = computed(() => filterSchoolRows(props.devices));
const props = defineProps({ devices: { type: Array, required: true } });
const emit = defineEmits(["close"]);
const dialog = ref(null);
const bodyPanel = ref(null);
const region = ref(null);
const school = ref(null);
const page = ref(1);
const deviceMode = computed(() => adminScope.restricted || !!school.value);
const provider = computed(() =>
  createLocalStatisticsProvider(scopedDevices.value)
);
const rows = computed(() =>
  adminScope.restricted
    ? scopedDevices.value
        .map(normalizeDevice)
        .sort(
          (a, b) =>
            (b.seconds ?? -1) - (a.seconds ?? -1) ||
            a.uuid.localeCompare(b.uuid)
        )
    : school.value
    ? provider.value.devices(region.value.key, school.value.key)
    : region.value
    ? provider.value.schools(region.value.key)
    : provider.value.regions()
);
const pageResult = computed(() => paginate(rows.value, page.value));
const heading = computed(() =>
  adminScope.restricted
    ? `[${adminScope.schoolName}] 기기별 사용시간`
    : deviceMode.value
    ? "기기별 사용시간"
    : region.value
    ? "학교별 기기 사용시간 분포"
    : "지역별 기기 사용시간 분포"
);
const schoolTitle = computed(() =>
  school.value ? groupTitle(school.value) : ""
);
const unknownCount = computed(() =>
  rows.value.reduce(
    (sum, row) =>
      sum + (deviceMode.value ? Number(row.seconds === null) : row.unknown),
    0
  )
);
const legend = computed(() =>
  unknownCount.value ? [...USAGE_BUCKETS, UNKNOWN_BUCKET] : USAGE_BUCKETS
);
const axisMax = computed(() => schoolAxisMax(rows.value));
const chartKey = computed(
  () =>
    `${region.value?.key || "regions"}:${school.value?.key || "schools"}:${
      pageResult.value.page
    }`
);
const chartData = computed(() =>
  deviceMode.value
    ? deviceChartData(pageResult.value.items)
    : distributionChartData(
        pageResult.value.items.map((row) => ({
          ...row,
          label: groupTitle(row)
        }))
      )
);
watch(chartKey, async () => {
  await nextTick();
  if (bodyPanel.value) bodyPanel.value.scrollTop = 0;
});
const chartOptions = computed(() =>
  statisticsChartOptions({
    deviceMode: deviceMode.value,
    axisMax: axisMax.value,
    groups: pageResult.value.items,
    devices: pageResult.value.items,
    select: selectGroup
  })
);
const chartPlugins = computed(() => [
  statisticsLabelsPlugin({
    deviceMode: deviceMode.value,
    devices: pageResult.value.items
  })
]);
function countAt(item, index) {
  return index === USAGE_BUCKETS.length ? item.unknown : item.counts[index];
}
function groupTitle(item) {
  return item.label;
}
function goRegions() {
  if (adminScope.restricted) return;
  region.value = null;
  school.value = null;
  page.value = 1;
}
function goSchools() {
  if (adminScope.restricted) return;
  school.value = null;
  page.value = 1;
}
function selectGroup(group) {
  if (adminScope.restricted) return;
  if (!region.value) region.value = group;
  else school.value = group;
  page.value = 1;
}
function changePage(delta) {
  page.value = pageResult.value.page + delta;
}
function close() {
  emit("close");
}
watch(() => props.devices, goRegions);
let previousFocus;
let previousOverflow;
onMounted(async () => {
  previousFocus = document.activeElement;
  previousOverflow = document.body.style.overflow;
  document.body.style.overflow = "hidden";
  await nextTick();
  dialog.value?.focus();
});
onBeforeUnmount(() => {
  document.body.style.overflow = previousOverflow;
  previousFocus?.focus();
});
function handleKeydown(event) {
  if (event.key === "Escape") {
    event.preventDefault();
    close();
    return;
  }
  if (event.key !== "Tab") return;
  const focusable = [
    ...dialog.value.querySelectorAll(
      'button:not([disabled]), summary, [tabindex="0"]'
    )
  ].filter((element) => element.getClientRects().length);
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (
    event.shiftKey &&
    (document.activeElement === first ||
      document.activeElement === dialog.value)
  ) {
    event.preventDefault();
    last?.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first?.focus();
  }
}
</script>

<style scoped>
.usage-statistics-overlay {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgb(0 0 0 / 55%);
}
.usage-statistics-dialog {
  width: min(1320px, 100%);
  max-height: calc(100dvh - 48px);
  display: flex;
  flex-direction: column;
  background: #fff;
  color: #262626;
  border: 1px solid #d4d4d4;
  font-family: inherit;
}
.statistics-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 20px 24px;
  border-bottom: 1px solid #ddd;
}
.statistics-header h2 {
  margin: 0;
  font-size: 21px;
}
.statistics-header p,
.statistics-note {
  font-size: 13px;
  line-height: 1.6;
  color: #666;
  margin: 8px 0;
}
.statistics-button {
  border: 1px solid #ccc;
  background: white;
  color: #262626;
  padding: 9px 15px;
  cursor: pointer;
  border-radius: 0;
  white-space: nowrap;
}
.statistics-button:disabled {
  color: #aaa;
  cursor: default;
}
.statistics-breadcrumb {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  padding: 14px 24px;
  border-bottom: 1px solid #ddd;
  font-size: 14px;
  overflow-wrap: anywhere;
}
.statistics-breadcrumb button,
.statistics-select {
  border: 0;
  background: transparent;
  color: #29546d;
  font: inherit;
  text-align: left;
  text-decoration: underline;
  cursor: pointer;
  padding: 4px;
  overflow-wrap: anywhere;
}
.statistics-breadcrumb [aria-current="page"] {
  color: #262626;
  text-decoration: none;
  font-weight: 600;
}
.statistics-body {
  overflow: auto;
  min-height: 0;
  padding: 20px 24px;
}
.statistics-body h3 {
  margin: 0 0 8px;
  font-size: 17px;
}
.statistics-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 18px;
  list-style: none;
  padding: 0;
  margin: 16px 0;
  font-size: 12px;
}
.statistics-legend li {
  display: flex;
  align-items: center;
  gap: 6px;
}
.statistics-legend li span {
  width: 13px;
  height: 13px;
  border: 1px solid rgb(0 0 0 / 8%);
}
.statistics-chart-scroll,
.statistics-table-scroll {
  overflow-x: auto;
}
.statistics-chart {
  position: relative;
  min-width: 820px;
}
.statistics-pagination {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin: 18px 0;
  font-size: 13px;
}
.statistics-pagination > span {
  margin-right: auto;
}
.statistics-details {
  border-top: 1px solid #ddd;
  padding-top: 14px;
  font-size: 13px;
}
.statistics-details summary {
  cursor: pointer;
  margin-bottom: 12px;
}
.statistics-details table {
  border-collapse: collapse;
  width: 100%;
  min-width: 800px;
}
.statistics-details th,
.statistics-details td {
  border-bottom: 1px solid #e5e5e5;
  padding: 10px;
  text-align: left;
  white-space: nowrap;
  font-weight: normal;
}
.statistics-details thead th {
  background: #f5f5f5;
}
.statistics-details tbody th {
  white-space: normal;
  min-width: 160px;
  max-width: 340px;
  overflow-wrap: anywhere;
}
.statistics-empty {
  text-align: center;
  padding: 60px 10px;
  color: #666;
}
.statistics-sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip-path: inset(50%);
}
button:focus-visible,
summary:focus-visible {
  outline: 2px solid #41748e;
  outline-offset: 2px;
}
@media (max-width: 640px) {
  .usage-statistics-overlay {
    padding: 8px;
  }
  .usage-statistics-dialog {
    max-height: calc(100dvh - 16px);
  }
  .statistics-header,
  .statistics-body {
    padding: 16px 12px;
  }
  .statistics-breadcrumb {
    padding: 12px;
  }
  .statistics-header h2 {
    font-size: 18px;
  }
}
</style>
